import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';

import { useAppDispatch, useAppSelector } from '../../store';
import { findOrder, getOrder } from '../../store/order/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/order/orderSlice';
import Loader from '../../components/Loader';
import {
  reset as resetOrder,
  setStatus as setOrderStatus,
} from '../../store/order/orderSlice';

import { encryptString } from '../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { selectPaymentStatusColor } from '../../utils/selectPaymentStatusColor';
import { fDateTime, formatDate } from '../../utils/formatTime';
import { CSVLink } from 'react-csv';
import {
  CSV_ORDER_HEADERS,
  ORDER_STATUS,
  POS_ORDER_HEADERS,
} from '../../constants';
import { IsNotNullOrEmpty, isValidArray } from '../../utils/valid';
import { csv } from './CSV';
import { findOrderExport } from '../../store/orderExport/api';
import { fileNameGeneraterWithoutType } from '../../utils/fileNameGenerater';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { searchDateFormat } from '../../utils/searchDateFormat';
import Iconify from '../../components/Iconify';
import { setStatus as setNotifcationStatus } from '../../store/notification/notificationSlice';
import Breadcrumbs from '../../components/Breadcrumbs';
import { pos } from './POS';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getDownloadInvoice } from '../../store/downloadInvoice/api';
import { OrderStatus } from '../../enum';
import { createOrderStatusChange } from '../../store/orderStatusChange/api';
import useToggle from '../../hooks/useToggle';
import CloseIcon from '@mui/icons-material/Close';
import { Order } from '../../store/order/api/interface';
import { refreshPendingOrdersCount } from '../../store/pendingOrdersCount/pendingOrdersCountSlice';
import {
  ExactDeliverySlot,
  RegenerateOrderPaymentLink,
} from '../../client/services';
import ExactDeliverySlotSelector, { ExactDeliverySlotSelectorRef } from '../../components/ExactDeliverySlotSelector';
import TodayAvailableSlots from './components/TodayAvailableSlots';
import DeliverySlotsCalendarDialog from '../DeliveryTimeSlots/DeliverySlotsCalendarDialog';
import {
  ORDER_SOURCE_OPTIONS,
  formatOrderSourceLabel,
} from '../../utils/orderSource';
import { toast } from 'react-toastify';

interface OrderListProps {
  /** When true, show Temporary Orders listing (isTempOrder=true) */
  tempOrdersOnly?: boolean;
}

const Index = ({ tempOrdersOnly = false }: OrderListProps) => {
  const dispatch = useAppDispatch();
  const exportCsvElement = useRef<any>();
  const exportPosElement = useRef<any>();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.order);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const [search, setSearch] = useState<string>('');
  const [csvStructureData, setCsvStructureData] = useState<any>([]);
  const [posStructureData, setPosStructureData] = useState<any>([]);
  const [orderStatus, setOrderStatusState] = useState<string[]>(
    tempOrdersOnly ? [] : ['pending', 'confirmed', 'shipped']
  );
  const [orderDate, setOrderDate] = useState<Date | null>();
  const [orderSource, setOrderSource] = useState<string>('');
  const [serviceLineStatus, setServiceLineStatus] = useState<string>('');
  const handleServiceLineStatusChange = (value: string) => {
    setServiceLineStatus(value);
  };
  const { toggle, onOpen, onClose } = useToggle();
  const [refundForCancel, setRefundForCancel] = useState<boolean>(true);
  const [orderDetails, setOrderDetails] = useState<Order>();
  const [exactDeliverySlotDialogOpen, setExactDeliverySlotDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{orderId: string, status: string} | null>(null);
  const exactDeliverySlotRef = useRef<ExactDeliverySlotSelectorRef>(null);
  const [savingSlot, setSavingSlot] = useState(false);
  const [slotsCalendarOpen, setSlotsCalendarOpen] = useState(false);
  const [regeneratingOrderId, setRegeneratingOrderId] = useState<string | null>(
    null
  );
  const [paymentLinkDialog, setPaymentLinkDialog] = useState<{
    open: boolean;
    url: string;
  }>({ open: false, url: '' });

  const viewBasePath = tempOrdersOnly ? '/temp-order' : '/order';
  const editBasePath = tempOrdersOnly ? '/temp-order' : '/order';

  const buildListQuery = (overrides: Record<string, unknown> = {}) => ({
    limit,
    skip,
    ...(tempOrdersOnly ? { isTempOrder: true } : {}),
    ...(search && { search }),
    ...(!tempOrdersOnly && orderStatus.length > 0 && { status: orderStatus }),
    ...(orderDate && { orderDate: searchDateFormat(orderDate) }),
    ...(orderSource && { orderSource }),
    ...(!tempOrdersOnly &&
      serviceLineStatus && { serviceLineStatus }),
    ...overrides,
  });

  useEffect(() => {
    dispatch(resetOrder());
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  }, [dispatch, tempOrdersOnly]);

  useEffect(() => {
    const queryParams = buildListQuery();
    console.log('Frontend sending query params:', queryParams);
    dispatch(findOrder(queryParams));
  }, [
    dispatch,
    limit,
    skip,
    orderStatus,
    search,
    orderDate,
    orderSource,
    serviceLineStatus,
    tempOrdersOnly,
  ]);

  useEffect(() => {
    dispatch(resetOrder());
  }, [search]);

  useEffect(() => {
    dispatch(setNotifcationStatus({ status: 'idle' }));
  }, []);

  // Refresh pending orders count when orders page loads
  useEffect(() => {
    dispatch(refreshPendingOrdersCount());
  }, [dispatch]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setOrderStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleChangeStatus = async (value: string, order: Order) => {
    if (value && order?.id) {
      // Skip if selecting the same current status
      if (order.status === value) {
        return;
      }
      if (value !== OrderStatus.cancelled) {
        // If moving to confirmed (preparing in UI), check if exact delivery slot exists
        if (value === 'confirmed') {
          try {
            const result = await ExactDeliverySlot.find({ query: { orderId: order.id } });
            const hasExactSlot = Boolean(result && result.exactDeliverySlot);
            if (!hasExactSlot) {
              // Show dialog to select time slot first
              setPendingStatusChange({ orderId: order.id, status: value });
              setExactDeliverySlotDialogOpen(true);
              return;
            }
          } catch (e) {
            // No slot found, show dialog to select time slot first
            setPendingStatusChange({ orderId: order.id, status: value });
            setExactDeliverySlotDialogOpen(true);
            return;
          }
        }
        await proceedWithStatusChange(value, order);
      } else {
        setOrderDetails(order);
        onOpen();
      }
    }
  };

  const proceedWithStatusChange = async (status: string, order: Order) => {
    if (order?.id) {
      // Avoid sending invalid or duplicate transitions
      if (order.status === status) {
        return;
      }
      try {
        const result = await dispatch(
          createOrderStatusChange({ orderId: order?.id, status })
        ).unwrap();
        if (IsNotNullOrEmpty(result)) {
          toast.success('Order status updated');
          dispatch(findOrder(buildListQuery()));
          // Refresh pending orders count after status change
          dispatch(refreshPendingOrdersCount());
        }
      } catch {
        // Error toast already shown by createOrderStatusChange
      }
    }
  };

  const handleExactSlotDialogClose = () => {
    setExactDeliverySlotDialogOpen(false);
    setPendingStatusChange(null);
  };

  const handleExactSlotSaved = async () => {
    setExactDeliverySlotDialogOpen(false);
    if (pendingStatusChange) {
      // Find the order object
      const orderId = pendingStatusChange.orderId;
      let latestOrder = data.find(o => o.id === orderId);
      try {
        // Ensure we have the latest status from backend
        const record = await dispatch(getOrder({ id: orderId })).unwrap();
        if (record) {
          latestOrder = record as any;
        }
      } catch {}

      if (latestOrder && latestOrder.status !== pendingStatusChange.status) {
        await proceedWithStatusChange(pendingStatusChange.status, latestOrder as any);
      }
      setPendingStatusChange(null);
    }
  };

  const handleSaveSlot = async () => {
    if (exactDeliverySlotRef.current) {
      setSavingSlot(true);
      try {
        await exactDeliverySlotRef.current.handleSave();
        // Dialog will close via onSlotUpdate callback -> handleExactSlotSaved
      } finally {
        setSavingSlot(false);
      }
    }
  };

  const handleYes = async () => {
    if (orderDetails?.id) {
      try {
        const result = await dispatch(
          createOrderStatusChange({
            orderId: orderDetails?.id,
            status: OrderStatus.cancelled,
            isRefund: refundForCancel,
          })
        ).unwrap();
        if (IsNotNullOrEmpty(result)) {
          toast.success('Order cancelled');
          dispatch(findOrder(buildListQuery()));
          // Refresh pending orders count after cancellation
          dispatch(refreshPendingOrdersCount());
        }
      } catch {
        // Error toast already shown by createOrderStatusChange
      }
    }
    setRefundForCancel(true);
    onClose();
  };

  const handleClose = () => {
    setRefundForCancel(true);
    onClose();
  };

  const handleNo = async () => {
    setRefundForCancel(true);
    onClose();
  };

  const handleCsvExport = async () => {
    try {
      await dispatch(
        findOrderExport({
          ...(search && { search: search }),
          ...(orderStatus && { status: orderStatus }),
          ...(orderDate && { orderDate: searchDateFormat(orderDate) }),
          isExport: true,
        })
      ).then((data: any) => {
        setCsvStructureData(csv(data?.payload?.data));
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (
      isValidArray(csvStructureData) &&
      exportCsvElement &&
      exportCsvElement.current &&
      exportCsvElement.current.link
    ) {
      exportCsvElement.current.link.click();
      setCsvStructureData([]);
    }
  }, [isValidArray(csvStructureData)]);

  const handlePosExport = async () => {
    try {
      await dispatch(
        findOrderExport({
          ...(search && { search: search }),
          ...(orderStatus && { status: orderStatus }),
          ...(orderDate && { orderDate: searchDateFormat(orderDate) }),
          isExport: true,
        })
      ).then((data: any) => {
        setPosStructureData(pos(data?.payload?.data));
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (
      isValidArray(posStructureData) &&
      exportPosElement &&
      exportPosElement.current &&
      exportPosElement.current.link
    ) {
      exportPosElement.current.link.click();
      setPosStructureData([]);
    }
  }, [isValidArray(posStructureData)]);

  const base64ToBlob = (base64: any, mime: string) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  };

  const handleRegeneratePaymentLink = async (orderId?: string) => {
    if (!orderId || regeneratingOrderId) return;
    setRegeneratingOrderId(orderId);
    try {
      const result: any = await RegenerateOrderPaymentLink.create({ orderId });
      if (result?.paymentUrl) {
        setPaymentLinkDialog({ open: true, url: result.paymentUrl });
        toast.success(
          result.whatsappSent
            ? 'Payment link regenerated and sent via WhatsApp'
            : 'Payment link regenerated (WhatsApp not sent — check customer phone)'
        );
      } else {
        toast.warn('Payment link regenerated but no URL was returned');
      }
    } catch (err: any) {
      toast.error(
        err?.message ||
          err?.data?.message ||
          'Failed to regenerate payment link'
      );
    } finally {
      setRegeneratingOrderId(null);
    }
  };

  const handleCopyPaymentLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLinkDialog.url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleDownloadInvoice = async (id: string) => {
    try {
      const result = await dispatch(getDownloadInvoice({ id: id })).unwrap();
      if (IsNotNullOrEmpty(result)) {
        const pdfBlob = base64ToBlob(result, 'application/pdf');
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url);
      }
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };

  return (
    <Page title={tempOrdersOnly ? 'Temporary Orders: List' : 'Order: List'}>
      <Container maxWidth={'xl'}>
        <Grid
          container
          sx={{ mb: 5 }}
          spacing={2}
        >
          <Grid
            item
            md={6}
            sm={12}
          >
            <Typography
              variant='h4'
              gutterBottom
            >
              {tempOrdersOnly ? 'Temporary Orders' : 'Order'}
            </Typography>
            <Breadcrumbs links={[{ name: 'Dashboard', href: '/' }]} />
          </Grid>

          <Grid
            item
            md={6}
            sm={12}
            container
            alignItems='center'
            justifyContent='flex-end'
            gap={2}
          >
            {(userRole === 'admin' || userRole === 'orderManager') && (
              <>
                <CSVLink
                  ref={exportCsvElement}
                  headers={CSV_ORDER_HEADERS}
                  data={csvStructureData}
                  filename={fileNameGeneraterWithoutType('Orders', 'csv')}
                  style={{ textDecoration: 'none' }}
                  asyncOnClick={true}
                />
                <Tooltip title={'Export to download'}>
                  <Button
                    variant='contained'
                    startIcon={<i className='fa-solid fa-file-csv'></i>}
                    onClick={handleCsvExport}
                  >
                    {'Export CSV'}
                  </Button>
                </Tooltip>
              </>
            )}

            {(userRole === 'admin' || userRole === 'orderManager') && (
              <>
                <CSVLink
                  ref={exportPosElement}
                  headers={POS_ORDER_HEADERS}
                  data={posStructureData}
                  filename={fileNameGeneraterWithoutType('pos.order', 'csv')}
                  style={{ textDecoration: 'none' }}
                  asyncOnClick={true}
                />
                <Tooltip title={'Export to download'}>
                  <Button
                    variant='contained'
                    startIcon={<i className='fa-solid fa-file-csv'></i>}
                    onClick={handlePosExport}
                  >
                    {'POS Export'}
                  </Button>
                </Tooltip>
              </>
            )}

            {!tempOrdersOnly &&
              (userRole === 'admin' || userRole === 'orderManager') && (
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                component={Link}
                to='/order/create'
                sx={{
                  backgroundColor: '#EE3750 !important',
                  '&:hover': {
                    backgroundColor: '#EE3750 !important'
                  }
                }}
              >
                {'Create Order'}
              </Button>
            )}
          </Grid>
        </Grid>

        {!tempOrdersOnly && (
          <>
            <TodayAvailableSlots
              headerAction={
                <Button
                  variant='text'
                  startIcon={<Iconify icon='mdi:calendar-month' />}
                  onClick={() => setSlotsCalendarOpen(true)}
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    minWidth: 0,
                    fontWeight: 600,
                    color: 'primary.main',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  View Capacity Calendar
                </Button>
              }
            />

            <DeliverySlotsCalendarDialog
              open={slotsCalendarOpen}
              onClose={() => setSlotsCalendarOpen(false)}
            />
          </>
        )}

        <Card>
          <Box
            sx={{
              display: 'flex',
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              alignItems: 'center',
              gap: 1.5,
              py: 2.5,
              px: 3,
            }}
          >
            {!tempOrdersOnly && (
              <FormControl
                size='small'
                sx={{ flex: '1 1 320px', minWidth: 280, maxWidth: 380 }}
              >
                <InputLabel id='status'>{'Status'}</InputLabel>
                <Select
                  name='status'
                  labelId='status'
                  id='status'
                  multiple
                  value={orderStatus}
                  label={'Status'}
                  onChange={(e) => setOrderStatusState(e.target.value as string[])}
                  MenuProps={{
                    style: {
                      maxHeight: 400,
                    },
                  }}
                  sx={{ textTransform: 'capitalize' }}
                  renderValue={(selected) => {
                    const selectedStatuses = ORDER_STATUS.filter((status) =>
                      (selected as string[]).includes(status.value)
                    );
                    return selectedStatuses.map((status) => status.name).join(', ');
                  }}
                >
                  {ORDER_STATUS?.map((status) => (
                    <MenuItem
                      key={status.name}
                      value={status.value}
                      sx={{
                        textTransform: 'capitalize',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{status.name}</span>
                      {orderStatus.includes(status.value) && (
                        <span
                          style={{
                            color: '#EE3750',
                            fontWeight: 'bold',
                            backgroundColor: '#FDF0F1',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl
              size='small'
              sx={{ flex: '0 1 160px', minWidth: 140, maxWidth: 180 }}
            >
              <InputLabel id='orderSource' shrink>
                {'Source'}
              </InputLabel>
              <Select
                name='orderSource'
                labelId='orderSource'
                id='orderSource'
                value={orderSource}
                label={'Source'}
                displayEmpty
                notched
                onChange={(e) => setOrderSource(String(e.target.value))}
                renderValue={(selected) => {
                  if (!selected) {
                    return 'All';
                  }
                  return (
                    ORDER_SOURCE_OPTIONS.find(
                      (option) => option.value === selected
                    )?.label || String(selected)
                  );
                }}
              >
                <MenuItem value=''>All</MenuItem>
                {ORDER_SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: '0 1 170px', minWidth: 150, maxWidth: 190 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={'Order Date'}
                  value={orderDate ?? ''}
                  onChange={(value: Date | null) => {
                    setOrderDate(value);
                  }}
                  inputFormat='dd/MM/yyyy'
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size='small'
                      name='startDate'
                      error={false}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <TextField
              size='small'
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                tempOrdersOnly
                  ? 'Search Transaction ID or Dibsy id'
                  : 'Search order no., Transaction ID, or Dibsy id'
              }
              sx={{ flex: '1 1 220px', minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Iconify
                      icon={'eva:search-fill'}
                      sx={{ color: 'text.disabled', width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {!tempOrdersOnly && (
              <FormControl
                size='small'
                sx={{ flex: '0 1 170px', minWidth: 150, maxWidth: 190 }}
              >
                <InputLabel id='serviceLineStatus' shrink>
                  {'Partner Status'}
                </InputLabel>
                <Select
                  name='serviceLineStatus'
                  labelId='serviceLineStatus'
                  id='serviceLineStatus'
                  value={serviceLineStatus}
                  label={'Partner Status'}
                  displayEmpty
                  notched
                  onChange={(e) =>
                    handleServiceLineStatusChange(String(e.target.value))
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return 'All';
                    }
                    const option = [
                      { value: 'pending', label: 'Pending' },
                      { value: 'confirmed', label: 'Confirmed' },
                      { value: 'rejected', label: 'Rejected' },
                    ].find((item) => item.value === selected);
                    return option?.label || String(selected);
                  }}
                >
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='pending'>Pending</MenuItem>
                  <MenuItem value='confirmed'>Confirmed</MenuItem>
                  <MenuItem value='rejected'>Rejected</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    {tempOrdersOnly ? (
                      <TableCell align='center'>{'Transaction ID'}</TableCell>
                    ) : (
                      <>
                        <TableCell align='center'>{'Order No.'}</TableCell>
                        <TableCell align='center'>{'Transaction ID'}</TableCell>
                      </>
                    )}
                    <TableCell align='center'>{'Source'}</TableCell>
                    <TableCell align='center'>{'User'}</TableCell>
                    <TableCell align='center'>{'Order Date'}</TableCell>
                    {!tempOrdersOnly && (
                      <TableCell align='center'>{'Ready By?'}</TableCell>
                    )}
                    {/*<TableCell align='center'>{'Delivery Slot'}</TableCell> */}
                    <TableCell align='center'>{'Total Amount'}</TableCell>
                    <TableCell align='center'>{'Identity'}</TableCell>
                    {!tempOrdersOnly && (
                      <TableCell align='center'>{'Status'}</TableCell>
                    )}
                    <TableCell align='center'>{'View'}</TableCell>
                    <TableCell align='center'>{'Edit'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={11}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((order) => (
                      <TableRow key={order.id}>
                        {tempOrdersOnly ? (
                          <TableCell align='center'>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              {order?.isLate && (
                                <Tooltip title='Order is late'>
                                  <AccessTimeIcon sx={{ color: 'error.main', fontSize: '18px' }} />
                                </Tooltip>
                              )}
                              {order?.transactionId || '-'}
                            </Box>
                          </TableCell>
                        ) : (
                          <>
                            <TableCell align='center'>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                {order?.isLate && (
                                  <Tooltip title='Order is late'>
                                    <AccessTimeIcon sx={{ color: 'error.main', fontSize: '18px' }} />
                                  </Tooltip>
                                )}
                                {order?.orderNumber || '-'}
                              </Box>
                            </TableCell>
                            <TableCell align='center'>
                              {order?.transactionId || '-'}
                            </TableCell>
                          </>
                        )}
                        <TableCell align='center'>
                          {formatOrderSourceLabel(order?.orderSource)}
                        </TableCell>
                        <TableCell align='center'>
                          {order?.userDetails?.firstName}{' '}
                          {order?.userDetails?.lastName}
                        </TableCell>
                        <TableCell align='center'>
                          {fDateTime(order?.orderDate)}
                        </TableCell>
                        {!tempOrdersOnly && (
                          <TableCell align='center'>
                            {/* Old flow: kitchen "Ready By" from exact_delivery_slot (not customer slot) */}
                            {order?.exactDeliverySlot && order?.exactDeliverySlotDate
                              ? `${format(new Date(order.exactDeliverySlotDate), 'dd MMM yyyy')} ${order.exactDeliverySlot.split(' - ')[0].replace(':00', '')}`
                              : order?.exactDeliverySlot
                              ? order.exactDeliverySlot.split(' - ')[0].replace(':00', '')
                              : '-'}
                          </TableCell>
                        )}
                        <TableCell align='center'>
                          {parseFloat(
                            Number(order?.totalAmount ?? 0).toFixed(2)
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={
                              order?.isIdentitySecret ? 'success' : 'error'
                            }
                          >
                            {order?.isIdentitySecret ? 'Secret' : 'Not Secret'}
                          </Label>
                        </TableCell>
                        {!tempOrdersOnly && (
                          <TableCell align='center'>
                            {userRole === 'admin' ||
                            userRole === 'orderManager' ? (
                              <FormControl fullWidth>
                                <Select
                                  id='orderStatus'
                                  name='orderStatus'
                                  value={order?.status}
                                  onChange={(e) => {
                                    handleChangeStatus(e.target.value, order);
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-input': {
                                      padding: '9.5px 0px',
                                    },
                                  }}
                                  renderValue={(selected) => {
                                    const selectedStatus = ORDER_STATUS.find(
                                      (status) => status.value === selected
                                    );
                                    return (
                                      <span
                                        style={{
                                          color: selectedStatus
                                            ? selectedStatus.color
                                            : 'inherit',
                                          textTransform: 'capitalize',
                                        }}
                                      >
                                        {selectedStatus
                                          ? selectedStatus.name
                                          : selected}
                                      </span>
                                    );
                                  }}
                                >
                                  {ORDER_STATUS.map((status) => {
                                    return (
                                      <MenuItem
                                        key={status?.value}
                                        value={status?.value}
                                        sx={{
                                          textTransform: 'capitalize',
                                          color: `${status?.color} !important`,
                                        }}
                                      >
                                        {status?.name}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            ) : (
                              <Label
                                variant='ghost'
                                color={selectPaymentStatusColor(order?.status)}
                              >
                                {order?.status === 'shipped'
                                  ? 'Out for Delivery'
                                  : order?.status === 'confirmed'
                                    ? 'Preparing'
                                    : order?.status}
                              </Label>
                            )}
                          </TableCell>
                        )}
                        <TableCell align='center' sx={{ verticalAlign: 'middle' }}>
                          <IconButton
                            size='small'
                            aria-label='visibility'
                            sx={{ color: '#808080' }}
                          >
                            <Link
                              to={`${viewBasePath}/view/${encryptString(order?.id ?? '')}`}
                              style={{
                                color: 'inherit',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <RemoveRedEyeIcon sx={{ fontSize: 20 }} />
                            </Link>
                          </IconButton>
                        </TableCell>
                        <TableCell align='center' sx={{ verticalAlign: 'middle' }}>
                          <Tooltip
                            title={'Edit Order'}
                            placement='top'
                          >
                            <IconButton
                              onClick={() => {
                                window.location.href = `${editBasePath}/edit/${encryptString(order?.id ?? '')}`;
                              }}
                              size='small'
                              sx={{ color: '#808080' }}
                            >
                              <Iconify icon={'eva:edit-outline'} sx={{ width: 20, height: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align='center'>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            {tempOrdersOnly && (
                              <Tooltip
                                title='Regenerate payment link & send WhatsApp'
                                placement='top'
                              >
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={
                                      regeneratingOrderId === order?.id
                                    }
                                    onClick={() =>
                                      handleRegeneratePaymentLink(order?.id)
                                    }
                                    sx={{ color: '#25D366' }}
                                  >
                                    {regeneratingOrderId === order?.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <WhatsAppIcon sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={'Download Invoice'}
                              placement='top'
                            >
                              <IconButton
                                size='small'
                                onClick={() => handleDownloadInvoice(order?.id)}
                                sx={{ color: '#808080' }}
                              >
                                <FileDownloadIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        <Dialog
          fullWidth
          open={toggle}
          onClose={handleClose}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle id='alert-dialog-title'>Order Cancel</DialogTitle>
          <IconButton
            aria-label='close'
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <Typography
              variant='h6'
              gutterBottom
            >
              Are you sure you want to cancel the order{' '}
              {orderDetails?.orderNumber}?
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  name='activeState'
                  onChange={(e) => setRefundForCancel(e.target.checked)}
                  checked={refundForCancel}
                />
              }
              label={'Refund'}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleYes}
              variant='contained'
            >
              Yes
            </Button>
            <Button
              onClick={handleNo}
              variant='outlined'
            >
              No
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={exactDeliverySlotDialogOpen} 
          onClose={handleExactSlotDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Edit Delivery Time Slot
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update the delivery time slot for this order
            </Typography>
            {pendingStatusChange && (
              <>
                <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <ExactDeliverySlotSelector 
                      ref={exactDeliverySlotRef}
                      orderId={pendingStatusChange.orderId} 
                      deliveryDate={data.find(o => o.id === pendingStatusChange.orderId)?.deliveryDate?.date}
                      allowEdit={true}
                      hideSaveButton={true}
                      onSlotUpdate={() => {
                        // Slot was saved, proceed with status change
                        handleExactSlotSaved();
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, p: 2, backgroundColor: '#FDF0F1', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Delivery Date:</strong> {pendingStatusChange && data.find(o => o.id === pendingStatusChange.orderId)?.deliveryDate?.date && formatDate(data.find(o => o.id === pendingStatusChange.orderId)?.deliveryDate?.date || '')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Delivery Time:</strong> {pendingStatusChange && data.find(o => o.id === pendingStatusChange.orderId)?.deliveryDate?.time}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                  <strong>Note:</strong> If you select 3 PM - 4 PM timeslot, order must be ready at 3 PM. If order will not be ready at 3:15 PM, it will be considered as late.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleExactSlotDialogClose} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSlot} 
              variant="contained"
              sx={{ 
                backgroundColor: '#EE3750 !important', 
                '&:hover': { 
                  backgroundColor: '#EE3750 !important' 
                },
                '&:disabled': {
                  backgroundColor: '#c4cdd5',
                  color: '#fff'
                }
              }}
              disabled={savingSlot}
            >
              {savingSlot ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={paymentLinkDialog.open}
          onClose={() => setPaymentLinkDialog({ open: false, url: '' })}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Payment link regenerated</DialogTitle>
          <DialogContent>
            <Typography variant='body2' sx={{ mb: 2 }}>
              A new payment link was created and WhatsApp was sent to the
              customer (when a phone number is available). You can also copy
              the link below.
            </Typography>
            <TextField
              fullWidth
              size='small'
              value={paymentLinkDialog.url}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='Copy payment link'
                      onClick={handleCopyPaymentLink}
                      edge='end'
                    >
                      <ContentCopyIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCopyPaymentLink}>Copy link</Button>
            <Button
              variant='contained'
              onClick={() => setPaymentLinkDialog({ open: false, url: '' })}
              sx={{
                backgroundColor: '#EE3750 !important',
                '&:hover': { backgroundColor: '#EE3750 !important' },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default Index;
